import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button, ButtonGroup, MenuItem, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import ContentDuplicateIcon from 'mdi-react/ContentDuplicateIcon';
import DeleteOutlineIcon from 'mdi-react/DeleteOutlineIcon';
import RotateLeftIcon from 'mdi-react/RotateLeftIcon';
import RotateRightIcon from 'mdi-react/RotateRightIcon';
import SelectGroupIcon from 'mdi-react/SelectGroupIcon';
import { SKETCH_EXTENT } from '../models/floorplan-sketch';
import { editorTheme } from '../utils/editor-theme';
import {
  REVIEW_KINDS, boxOfOutline, type ReviewItem, type ReviewKind,
} from '../utils/sketch-review';

interface Props {
  readonly selected: readonly ReviewItem[];
  readonly aspect: number;
  /** Metres across the picture, when the plan's size is known: sizes are then shown in metres. */
  readonly metresAcross: number | null;
  readonly onKind: (kind: ReviewKind) => void;
  readonly onText: (text: string) => void;
  /** Turns the selection by `degrees`, clockwise as drawn. */
  readonly onRotate: (degrees: number) => void;
  readonly onScale: (factor: number) => void;
  /** Sets the one selected find's size, in lengths of `x`. */
  readonly onResize: (length: number, breadth: number) => void;
  readonly onDuplicate: () => void;
  readonly onSelectSimilar: () => void;
  readonly onRemove: () => void;
}

const round = (value: number, places = 2): number => Math.round(value * (10 ** places)) / (10 ** places);

/** The plan's angle of a direction drawn on the picture: counter-clockwise, -90 to 90. */
const planAngleOf = (imageDegrees: number): number => {
  let degrees = -imageDegrees;
  while (degrees <= -90) degrees += 180;
  while (degrees > 90) degrees -= 180;
  return round(degrees, 1);
};

/** A number field that is only applied when it is left or Enter is pressed, so typing is not a change per key. */
const NumberField = ({
  label, value, onApply, step = 1,
}: { label: string; value: number; onApply: (value: number) => void; step?: number }): React.JSX.Element => {
  const [text, setText] = useState(String(value));
  const apply = (): void => {
    const parsed = Number(text);
    if (Number.isFinite(parsed) && parsed !== value) onApply(parsed);
    else setText(String(value));
  };

  return (
    <TextField
      size="small"
      type="number"
      label={label}
      value={text}
      inputProps={{ step }}
      onChange={(event): void => setText(event.target.value)}
      onBlur={apply}
      onKeyDown={(event): void => {
        if (event.key === 'Enter') {
          event.preventDefault();
          apply();
        }
      }}
      sx={{ flex: 1 }}
    />
  );
};

/**
 * What can be done to what is selected in the image review: say what it is, name it, turn it,
 * size it, copy it, find the ones like it, or remove it. One find or many alike.
 */
const ImageReviewSelection = ({
  selected, aspect, metresAcross, onKind, onText, onRotate, onScale, onResize, onDuplicate, onSelectSimilar, onRemove,
}: Props): React.JSX.Element => {
  const { t } = useTranslation();

  if (!selected.length) {
    return <Typography variant="body2" sx={{ color: editorTheme.inkDim }}>{t('floorplan-image-review-nothing-selected')}</Typography>;
  }

  const [only] = selected;
  const single = selected.length === 1 ? only : undefined;
  const kinds = new Set(selected.map((item) => item.kind));
  const commonKind = kinds.size === 1 ? only.kind : '';
  const box = single ? boxOfOutline(single.outline, aspect) : undefined;

  // Sizes in metres when the plan's size is known, otherwise in the picture's own units.
  const perUnit = metresAcross ? metresAcross / SKETCH_EXTENT : null;
  const shown = (units: number): number => round(perUnit ? units * perUnit : units);
  const fromShown = (value: number): number => (perUnit ? value / perUnit : value);
  const unitKey = perUnit ? 'floorplan-image-review-metres' : 'floorplan-image-review-units';

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2">{t('floorplan-image-review-selected', { count: selected.length })}</Typography>

      <TextField
        select
        size="small"
        label={String(t('floorplan-image-review-this-is'))}
        value={commonKind}
        onChange={(event): void => onKind(event.target.value as ReviewKind)}
        helperText={commonKind ? undefined : String(t('floorplan-image-review-mixed'))}
      >
        {REVIEW_KINDS.map((kind) => (
          <MenuItem key={kind} value={kind}>{t(`floorplan-image-review-kind-${kind}`)}</MenuItem>
        ))}
      </TextField>

      {single && (single.kind === 'label' || single.kind === 'room') && (
        <TextField
          size="small"
          autoFocus
          label={String(t(single.kind === 'label' ? 'floorplan-image-review-label-text' : 'floorplan-image-review-room-name'))}
          value={single.text}
          onChange={(event): void => onText(event.target.value)}
        />
      )}

      <Stack spacing={0.75}>
        <Typography variant="caption" sx={{ color: editorTheme.inkDim }}>{t('floorplan-image-review-turn-heading')}</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <ButtonGroup size="small" variant="outlined">
            <Tooltip title={String(t('floorplan-image-review-turn-left'))}>
              <Button onClick={(): void => onRotate(-15)} aria-label={String(t('floorplan-image-review-turn-left'))}>
                <RotateLeftIcon size={16} />
              </Button>
            </Tooltip>
            <Tooltip title={String(t('floorplan-image-review-turn-right'))}>
              <Button onClick={(): void => onRotate(15)} aria-label={String(t('floorplan-image-review-turn-right'))}>
                <RotateRightIcon size={16} />
              </Button>
            </Tooltip>
            <Button onClick={(): void => onRotate(90)} sx={{ textTransform: 'none' }}>{t('floorplan-image-review-turn')}</Button>
          </ButtonGroup>
          {single && box && (
            <NumberField
              key={`angle-${single.id}-${planAngleOf(box.imageDegrees)}`}
              label={String(t('floorplan-image-review-angle'))}
              value={planAngleOf(box.imageDegrees)}
              // The plan's angle counts the other way round from the picture's.
              onApply={(angle): void => onRotate(-(angle - planAngleOf(box.imageDegrees)))}
            />
          )}
        </Stack>
      </Stack>

      <Stack spacing={0.75}>
        <Typography variant="caption" sx={{ color: editorTheme.inkDim }}>
          {t('floorplan-image-review-size-heading', { unit: String(t(unitKey)) })}
        </Typography>
        {single && box && (
          <Stack direction="row" spacing={1}>
            <NumberField
              key={`length-${single.id}-${shown(box.length)}`}
              label={String(t('floorplan-image-review-length'))}
              value={shown(box.length)}
              step={0.05}
              onApply={(value): void => onResize(fromShown(value), box.breadth)}
            />
            <NumberField
              key={`breadth-${single.id}-${shown(box.breadth)}`}
              label={String(t('floorplan-image-review-breadth'))}
              value={shown(box.breadth)}
              step={0.05}
              onApply={(value): void => onResize(box.length, fromShown(value))}
            />
          </Stack>
        )}
        <ButtonGroup size="small" variant="outlined">
          <Button onClick={(): void => onScale(1 / 1.1)} sx={{ textTransform: 'none' }}>{t('floorplan-image-review-shrink')}</Button>
          <Button onClick={(): void => onScale(1.1)} sx={{ textTransform: 'none' }}>{t('floorplan-image-review-grow')}</Button>
        </ButtonGroup>
      </Stack>

      <Stack direction="row" flexWrap="wrap" gap={1}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<ContentDuplicateIcon size={16} />}
          onClick={onDuplicate}
          sx={{ textTransform: 'none' }}
        >
          {t('floorplan-image-review-duplicate')}
        </Button>
        {single && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<SelectGroupIcon size={16} />}
            onClick={onSelectSimilar}
            sx={{ textTransform: 'none' }}
          >
            {t('floorplan-image-review-select-similar')}
          </Button>
        )}
        <Button
          size="small"
          color="error"
          variant="outlined"
          startIcon={<DeleteOutlineIcon size={16} />}
          onClick={onRemove}
          sx={{ textTransform: 'none' }}
        >
          {t('floorplan-image-review-remove')}
        </Button>
      </Stack>
    </Stack>
  );
};

export default ImageReviewSelection;
